import React, {useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {getActiveOfferMode} from 'modules/search/selectors'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {Button, Variant} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {NavBarTitle} from '@vio/atlas/NavBar'
import {Popover, PopoverPlacement} from '@vio/atlas/Popover'
import {Radio} from '@vio/atlas/Radio'
import {Text} from '@vio/atlas/Text'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useIsEmbedded} from '@vio/core/src/_web/utils/embedded/useIsEmbedded'
import {isFlutterAppWebView} from '@vio/core/src/_web/utils/flutter/isFlutterAppWebView'
import {selectIsInternalUser} from '@vio/core/src/auth/modules/selectors'
import {OFFER_MODES, type OfferMode} from '@vio/core/src/profile/constants'

import {applyOfferMode} from './applyOfferMode'

// Internal-only control: product terms, not translated
const TITLE = 'Offers mode'
const OFFER_MODE_LABELS: Record<OfferMode, string> = {
  meta: 'Meta',
  otaPromoted: 'OTA Promoted',
  otaFirst: 'OTA First',
  otaOnly: 'OTA Only'
}

interface Props {
  variant?: Variant
}

export const OfferModeSwitcher = ({variant = 'ghost'}: Props) => {
  const {
    internal: {hasOfferModeSwitcher}
  } = useBrandCapabilities()
  const isInternalUser = useSelector(selectIsInternalUser)
  const isEmbedded = useIsEmbedded()
  const activeOfferMode = useSelector(getActiveOfferMode)
  const {isMobile} = useDeviceLayout()
  const [isOpen, setIsOpen] = useState(false)
  const anchorRef = useRef(null)

  if (
    !hasOfferModeSwitcher ||
    !isInternalUser ||
    isEmbedded ||
    isFlutterAppWebView()
  )
    return null

  const close = () => setIsOpen(false)

  const options = (
    <div role="radiogroup" aria-label={TITLE}>
      {OFFER_MODES.map(mode => (
        <Box key={mode} paddingTop="s300" paddingBottom="s300">
          <Radio
            fullWidth
            name="offerMode"
            labelSize="bodyS"
            label={OFFER_MODE_LABELS[mode]}
            checked={mode === activeOfferMode}
            onChange={() => applyOfferMode(mode)}
          />
        </Box>
      ))}
    </div>
  )

  return (
    <Box display="flex">
      <Button
        ref={anchorRef}
        size="md"
        variant={variant}
        dataId="OfferModeSwitcher"
        onClick={() => setIsOpen(true)}
        iconStart={<Icon name="Settings" size={isMobile ? 'lg' : 'sm'} />}
      >
        {OFFER_MODE_LABELS[activeOfferMode]}
      </Button>
      {isMobile ? (
        <BottomSheet
          isOpen={isOpen}
          onClose={close}
          navBarProps={{
            centerAligned: true,
            withDivider: true,
            centerContent: <NavBarTitle>{TITLE}</NavBarTitle>
          }}
        >
          {options}
        </BottomSheet>
      ) : (
        <Popover
          isOpen={isOpen}
          anchorRef={anchorRef}
          onOutsideClick={close}
          placement={PopoverPlacement.BottomRight}
        >
          <Box
            width="240px"
            padding="s500"
            display="flex"
            flexDirection="column"
            gap="s600"
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text variant="titleM">{TITLE}</Text>
              <Button
                size="sm"
                variant="ghost"
                ariaLabel="Close"
                onClick={close}
                iconStart={<Icon name="Close" size="md" />}
              />
            </Box>
            {options}
          </Box>
        </Popover>
      )}
    </Box>
  )
}
